import { useEffect, useState, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { FaPlus, FaSearch } from 'react-icons/fa'
import { FiRefreshCw, FiX } from 'react-icons/fi'
import api from '../../../libs/axios'
import { useAuth } from '../../../provider/auth.provider'
import { useDebounce } from '../../../hooks/useDebounce'

interface BillingRecord {
  id: number
  billing_status: string
  billed_site_id?: string
  billed_ref_id?: string
  activity_amount?: number
  invoice_amount?: number
  created_at: string
  project_site?: { id: number; site_name: string; reference_number: string }
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-700',
  wcc_raised: 'bg-blue-100 text-blue-700',
  wcc_approved: 'bg-indigo-100 text-indigo-700',
  invoice_raised: 'bg-purple-100 text-purple-700',
  invoice_submitted: 'bg-yellow-100 text-yellow-700',
  paid: 'bg-green-100 text-green-700',
}

const BILLING_STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'wcc_raised', label: 'WCC Raised' },
  { value: 'wcc_approved', label: 'WCC Approved' },
  { value: 'invoice_raised', label: 'Invoice Raised' },
  { value: 'invoice_submitted', label: 'Invoice Submitted' },
  { value: 'paid', label: 'Paid' },
]

export default function VendorPortalBillings() {
  const { hasPermission } = useAuth()
  const canFileBilling = hasPermission('VendorPortal', 'view_billing')

  const [billings, setBillings] = useState<BillingRecord[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  // Debounced search — auto-fires 400 ms after typing stops
  const debouncedSearch = useDebounce(searchInput, 400)

  const fetchBillings = useCallback(() => {
    setLoading(true)
    const params: any = { page, limit: 20 }
    if (statusFilter) params.billing_status = statusFilter
    if (debouncedSearch) params.search = debouncedSearch
    api.get('/vendor-portal/billings', { params })
      .then(r => {
        setBillings(r.data.data?.rows ?? [])
        setTotal(r.data.data?.count ?? 0)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [page, statusFilter, debouncedSearch])

  useEffect(() => { fetchBillings() }, [fetchBillings])

  // Keep manual Search button working; debounce handles auto-search
  const handleSearch = () => { setSearch(searchInput); setPage(1) }
  const clearSearch = () => { setSearchInput(''); setSearch(''); setPage(1) }

  return (
    <>
      <Helmet><title>Vendor Portal – Billing</title></Helmet>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Billing</h1>
            <p className="text-sm text-gray-500">{total} billing records</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchBillings} className="flex items-center gap-1.5 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
              <FiRefreshCw className="w-4 h-4" /> Refresh
            </button>
            {canFileBilling && (
              <Link to="/vendor-portal/billings/eligible" className="flex items-center gap-1.5 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                <FaPlus className="w-3.5 h-3.5" /> File Billing
              </Link>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex items-center">
            <FaSearch className="absolute left-3 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search site ID / reference..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
            />
            {searchInput && (
              <button onClick={clearSearch} className="absolute right-2 text-gray-400 hover:text-gray-600">
                <FiX className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Search
          </button>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {BILLING_STATUSES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Site</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Site ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Ref</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Invoice Amt</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Filed On</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(7)].map((_, j) => (
                        <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>
                      ))}
                    </tr>
                  ))
                ) : billings.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-gray-400">No billing records found</td>
                  </tr>
                ) : billings.map((b, i) => (
                  <tr key={b.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 text-gray-400">{(page - 1) * 20 + i + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{b.project_site?.site_name ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{b.billed_site_id ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{b.billed_ref_id ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[b.billing_status] ?? 'bg-gray-100 text-gray-600'}`}>
                        {b.billing_status?.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {b.invoice_amount ? `₹${Number(b.invoice_amount).toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {new Date(b.created_at).toLocaleDateString('en-IN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > 20 && (
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
              <span>Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} of {total}</span>
              <div className="flex gap-2">
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border border-gray-200 rounded disabled:opacity-40">Prev</button>
                <button disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border border-gray-200 rounded disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
